/**
 * A job a kid ticks is not done until a parent approves it. Ticks a parent makes
 * are the approval. See docs/SPIRIT.md principle 9.
 */
export type TickState = 'open' | 'waiting' | 'done'

export function tickState(task: { done: boolean; approved: boolean }): TickState {
  if (!task.done) return 'open'
  return task.approved ? 'done' : 'waiting'
}

/**
 * What tapping a ticked-or-not job should do. A parent tapping a waiting job is
 * approving it, not unticking it — the kid did the work, so don't throw it away.
 */
/**
 * How a shared household job looks to someone who hasn't ticked it themselves.
 * Whoever takes it first owns it; nobody else can tick it, but a parent can
 * approve the taker's tick from here.
 */
export type ClaimState = 'free' | 'mine' | 'taken' | 'approvable'

export function claimState(
  task: { done: boolean; claimedBy: { approved: boolean } | null },
  byParent: boolean,
): ClaimState {
  if (task.done) return 'mine'
  if (!task.claimedBy) return 'free'
  return byParent && !task.claimedBy.approved ? 'approvable' : 'taken'
}

/** Says which parent approved a job, from the viewer's point of view. */
export function approvalLabel(approvedBy: { memberId: string; name: string } | null, viewerId: string | null): string {
  if (!approvedBy) return 'Approved by a parent'
  return approvedBy.memberId === viewerId ? 'Approved by you' : `Approved by ${approvedBy.name}`
}

/** A kid can't untick a job a parent has approved; only a parent can undo it. */
export function isLockedForKid(task: { done: boolean; approved: boolean }, byParent: boolean): boolean {
  return !byParent && tickState(task) === 'done'
}

export function nextTick(task: { done: boolean; approved: boolean }, byParent: boolean): boolean {
  return tickState(task) === 'waiting' && byParent ? true : !task.done
}
